import type {
  Service, Project, Pod, Deployment,
  InfrastructureNode, InfrastructureTree, HierarchyPreset,
  HealthScore, ExplorerNodeMetrics, ServiceStatus,
} from "@coremonit/types";
import { makeRng, int } from "./rng";

const PROVIDER_LABEL: Record<string, string> = {
  eks: "AWS", aks: "Azure", gke: "Google Cloud", ecs: "AWS ECS",
  kubernetes: "Kubernetes", docker: "Docker", vm: "VM",
};

function providerFor(type: string): string {
  if (type === "eks" || type === "ecs") return "AWS";
  if (type === "aks") return "Azure";
  if (type === "gke") return "Google Cloud";
  return "Kubernetes";
}

function healthFromStatus(status: ServiceStatus, rng: () => number): HealthScore {
  const base = status === "healthy" ? 92 + rng() * 7
    : status === "warning" ? 65 + rng() * 20
    : status === "critical" ? 20 + rng() * 35
    : 50 + rng() * 20;
  const score = Math.round(base);
  const grade = score >= 90 ? "A" : score >= 80 ? "B" : score >= 70 ? "C" : score >= 55 ? "D" : "F";
  return {
    score,
    grade,
    status,
    factors: [
      { label: "Availability", impact: status === "healthy" ? 0 : -12, detail: `${score}% SLO` },
      { label: "Error rate", impact: status === "critical" ? -25 : 0 },
      { label: "Resource pressure", impact: -Math.round(rng() * 8) },
    ],
  };
}

function rollupHealth(children: InfrastructureNode[]): HealthScore {
  if (!children.length) {
    return { score: 100, grade: "A", status: "healthy", factors: [] };
  }
  const avg = children.reduce((s, c) => s + c.health.score, 0) / children.length;
  const worst = children.reduce<ServiceStatus>((w, c) => {
    const order = { critical: 0, warning: 1, unknown: 2, healthy: 3 };
    return order[c.health.status] < order[w] ? c.health.status : w;
  }, "healthy");
  const score = Math.round(avg);
  const grade = score >= 90 ? "A" : score >= 80 ? "B" : score >= 70 ? "C" : score >= 55 ? "D" : "F";
  return { score, grade, status: worst, factors: [{ label: "Child rollup", impact: 0, detail: `${children.length} resources` }] };
}

function rollupMetrics(children: InfrastructureNode[]): ExplorerNodeMetrics {
  const n = Math.max(1, children.length);
  return {
    cpuPct: Math.round(children.reduce((s, c) => s + c.metrics.cpuPct, 0) / n),
    memPct: Math.round(children.reduce((s, c) => s + c.metrics.memPct, 0) / n),
    alertCount: children.reduce((s, c) => s + c.metrics.alertCount, 0),
    incidentCount: children.reduce((s, c) => s + c.metrics.incidentCount, 0),
    containerCount: children.reduce((s, c) => s + c.metrics.containerCount, 0),
    podCount: children.reduce((s, c) => s + (c.metrics.podCount ?? 0), 0),
  };
}

function makeNode(
  partial: Omit<InfrastructureNode, "children" | "health" | "metrics"> & {
    health?: HealthScore;
    metrics?: ExplorerNodeMetrics;
    children?: InfrastructureNode[];
  },
  rng: () => number,
): InfrastructureNode {
  const children = partial.children ?? [];
  return {
    ...partial,
    children,
    health: partial.health ?? (children.length ? rollupHealth(children) : healthFromStatus("healthy", rng)),
    metrics: partial.metrics ?? (children.length ? rollupMetrics(children) : {
      cpuPct: int(rng, 15, 75),
      memPct: int(rng, 20, 80),
      alertCount: rng() < 0.15 ? int(rng, 1, 3) : 0,
      incidentCount: rng() < 0.08 ? 1 : 0,
      containerCount: int(rng, 1, 24),
      podCount: int(rng, 1, 12),
      deploymentStatus: "synced",
      activeUsers: int(rng, 0, 500),
    }),
    tags: partial.tags ?? [],
    meta: partial.meta ?? {},
  };
}

function containerNodes(
  pod: Pod, svc: Service, rng: () => number,
): InfrastructureNode[] {
  const all = [...pod.containers, ...pod.sidecars, ...pod.initContainers];
  return all.map((c) => makeNode({
    id: `ctr_${pod.id}_${c.name}`,
    kind: "container",
    name: c.name,
    label: c.name,
    path: `/containers/${svc.id}/${pod.id}/${encodeURIComponent(c.name)}`,
    parentId: pod.id,
    tags: c.name.includes("sidecar") ? ["sidecar"] : [],
    meta: { image: c.image, restarts: c.restartCount },
    links: { serviceId: svc.id, podId: pod.id, containerName: c.name, clusterId: svc.deployment.cluster, namespace: pod.namespace },
    health: healthFromStatus(c.status === "running" ? "healthy" : c.status === "waiting" ? "warning" : "critical", rng),
    metrics: {
      cpuPct: Math.min(99, Math.round(c.cpuMillicoresLive / 10)),
      memPct: Math.min(99, Math.round(c.memoryMbLive / 12)),
      alertCount: 0,
      incidentCount: 0,
      containerCount: 1,
      podCount: 1,
    },
  }, rng));
}

function podNode(
  pod: Pod, svc: Service, dep: Deployment | undefined, rng: () => number,
): InfrastructureNode {
  const containers = containerNodes(pod, svc, rng);
  const st: ServiceStatus = pod.status === "running" ? "healthy" : pod.status === "pending" ? "warning" : "critical";
  return makeNode({
    id: pod.id,
    kind: "pod",
    name: pod.name,
    label: pod.name,
    path: `/containers/${svc.id}/${pod.id}`,
    parentId: dep?.id ?? svc.id,
    children: containers,
    tags: ["pod"],
    meta: { node: pod.node, restarts: pod.restartCount, ready: pod.ready },
    links: { serviceId: svc.id, podId: pod.id, deploymentId: dep?.id, clusterId: svc.deployment.cluster, namespace: pod.namespace },
    health: healthFromStatus(st, rng),
    metrics: {
      cpuPct: int(rng, 10, 70),
      memPct: int(rng, 15, 75),
      alertCount: pod.restartCount > 3 ? 1 : 0,
      incidentCount: pod.status === "crashloop" ? 1 : 0,
      containerCount: containers.length,
      podCount: 1,
    },
  }, rng);
}

function serviceNode(
  svc: Service, pods: Pod[], dep: Deployment | undefined, project: Project | undefined, rng: () => number,
): InfrastructureNode {
  const podChildren = pods.slice(0, 4).map((p) => podNode(p, svc, dep, rng));
  const depNode: InfrastructureNode | null = dep ? makeNode({
    id: dep.id,
    kind: "deployment",
    name: `${svc.name} · ${dep.revision}`,
    label: dep.revision,
    path: `/deployments?service=${svc.id}`,
    parentId: svc.id,
    children: podChildren,
    tags: [dep.rolloutPhase],
    meta: { image: dep.imageTag, gitCommit: dep.gitCommit.slice(0, 7) },
    links: { serviceId: svc.id, deploymentId: dep.id, gitCommit: dep.gitCommit, imageTag: dep.imageTag, clusterId: dep.cluster },
    health: healthFromStatus(dep.healthStatus, rng),
    metrics: {
      cpuPct: int(rng, 20, 65),
      memPct: int(rng, 25, 70),
      alertCount: dep.healthStatus !== "healthy" ? 1 : 0,
      incidentCount: 0,
      containerCount: podChildren.reduce((s, p) => s + p.children.length, 0),
      podCount: podChildren.length,
      deploymentStatus: dep.syncStatus,
    },
  }, rng) : null;

  const children = depNode ? [depNode] : podChildren;
  return makeNode({
    id: svc.id,
    kind: "microservice",
    name: svc.name,
    label: svc.name,
    path: `/services/${svc.id}`,
    parentId: project?.id,
    children,
    tags: svc.tags,
    meta: { team: svc.team, version: svc.version, language: svc.language },
    links: { serviceId: svc.id, projectId: svc.projectId, clusterId: svc.deployment.cluster, namespace: svc.deployment.namespace },
    health: healthFromStatus(svc.status, rng),
    metrics: {
      cpuPct: int(rng, 15, 80),
      memPct: int(rng, 20, 85),
      alertCount: svc.status !== "healthy" ? int(rng, 1, 2) : 0,
      incidentCount: svc.status === "critical" ? 1 : 0,
      containerCount: children.reduce((s, c) => s + c.metrics.containerCount, 0),
      podCount: svc.replicas.available,
      deploymentStatus: dep?.rolloutPhase ?? "idle",
      activeUsers: int(rng, 10, 800),
    },
  }, rng);
}

export function buildInfrastructureTree(
  services: Service[],
  projects: Project[],
  podsByService: Map<string, Pod[]>,
  deployments: Deployment[],
  preset: HierarchyPreset = "client_based",
): InfrastructureTree {
  const rng = makeRng(77);
  const depByService = new Map(deployments.map((d) => [d.serviceId, d]));
  const projectById = new Map(projects.map((p) => [p.id, p]));

  const k8sServices = services.filter((s) => s.deployment.cluster && s.deployment.type !== "vm");

  // Group: client (project) → environment → provider → cluster → namespace → project node → services
  type NamespaceServices = Service[];
  type ClusterNamespaces = Map<string, NamespaceServices>;
  type ProviderClusters = Map<string, ClusterNamespaces>;
  type EnvProviders = Map<string, ProviderClusters>;
  type ClientEnvironments = Map<string, EnvProviders>;
  const clientMap = new Map<string, ClientEnvironments>();

  for (const svc of k8sServices) {
    const proj = svc.projectId ? projectById.get(svc.projectId) : undefined;
    const clientKey = proj?.name ?? "Unassigned";
    const envKey = svc.environment;
    const provKey = providerFor(svc.deployment.type);
    const clusterKey = svc.deployment.cluster ?? "unknown";
    const nsKey = svc.deployment.namespace ?? "default";

    if (!clientMap.has(clientKey)) clientMap.set(clientKey, new Map());
    const envMap = clientMap.get(clientKey)!;
    if (!envMap.has(envKey)) envMap.set(envKey, new Map());
    const provMap = envMap.get(envKey)!;
    if (!provMap.has(provKey)) provMap.set(provKey, new Map());
    const clusterMap = provMap.get(provKey)!;
    if (!clusterMap.has(clusterKey)) clusterMap.set(clusterKey, new Map());
    const nsMap = clusterMap.get(clusterKey)!;
    if (!nsMap.has(nsKey)) nsMap.set(nsKey, []);
    nsMap.get(nsKey)!.push(svc);
  }

  const clientNodes: InfrastructureNode[] = [];

  for (const [clientName, envMap] of clientMap) {
    const envNodes: InfrastructureNode[] = [];
    for (const [envName, provMap] of envMap) {
      const provNodes: InfrastructureNode[] = [];
      for (const [provName, clusterMap] of provMap) {
        const clusterNodes: InfrastructureNode[] = [];
        for (const [clusterName, nsMap] of clusterMap) {
          const nsNodes: InfrastructureNode[] = [];
          for (const [nsName, svcs] of nsMap) {
            const projGroups = new Map<string, Service[]>();
            for (const s of svcs) {
              const pk = s.projectId ?? "none";
              if (!projGroups.has(pk)) projGroups.set(pk, []);
              projGroups.get(pk)!.push(s);
            }
            const projNodes: InfrastructureNode[] = [];
            for (const [pid, group] of projGroups) {
              const proj = pid !== "none" ? projectById.get(pid) : undefined;
              const svcNodes = group.map((s) => serviceNode(
                s,
                podsByService.get(s.id) ?? [],
                depByService.get(s.id),
                proj,
                rng,
              ));
              projNodes.push(makeNode({
                id: proj?.id ?? `proj_ns_${nsName}_${clientName}`,
                kind: "project",
                name: proj?.name ?? "Services",
                label: proj?.name ?? "Services",
                path: proj ? `/projects/${proj.key}` : `/services`,
                parentId: `ns_${clusterName}_${nsName}`,
                children: svcNodes,
                tags: proj ? [proj.key] : [],
                meta: { projectKey: proj?.key ?? "" },
                links: { projectId: proj?.id, clusterId: clusterName, namespace: nsName },
              }, rng));
            }
            nsNodes.push(makeNode({
              id: `ns_${clusterName}_${nsName}`,
              kind: "namespace",
              name: nsName,
              label: nsName,
              path: `/clusters/${clusterName}?namespace=${nsName}`,
              parentId: `cluster_${clusterName}`,
              children: projNodes,
              tags: ["namespace"],
              meta: { cluster: clusterName },
              links: { clusterId: clusterName, namespace: nsName },
            }, rng));
          }
          clusterNodes.push(makeNode({
            id: `cluster_${clusterName}`,
            kind: "cluster",
            name: clusterName,
            label: clusterName,
            path: `/clusters/${clusterName}`,
            parentId: `prov_${provName}_${envName}`,
            children: nsNodes,
            tags: ["kubernetes", "cluster"],
            meta: { provider: provName, environment: envName },
            links: { clusterId: clusterName },
          }, rng));
        }
        provNodes.push(makeNode({
          id: `prov_${provName}_${envName}_${clientName}`,
          kind: "cloud_provider",
          name: provName,
          label: provName,
          path: `/clusters?provider=${provName.toLowerCase()}`,
          parentId: `env_${envName}_${clientName}`,
          children: clusterNodes,
          tags: ["cloud"],
          meta: { provider: provName },
        }, rng));
      }
      envNodes.push(makeNode({
        id: `env_${envName}_${clientName}`,
        kind: "environment",
        name: envName.charAt(0).toUpperCase() + envName.slice(1),
        label: envName,
        path: `/services?env=${envName}`,
        parentId: `client_${clientName}`,
        children: provNodes,
        tags: [envName],
        meta: { environment: envName },
      }, rng));
    }
    clientNodes.push(makeNode({
      id: `client_${clientName.replace(/\s+/g, "_")}`,
      kind: "client",
      name: clientName,
      label: clientName,
      path: projects.find((p) => p.name === clientName) ? `/projects/${projects.find((p) => p.name === clientName)!.key}` : `/projects`,
      parentId: "org_root",
      children: envNodes,
      tags: ["client"],
      meta: { preset },
    }, rng));
  }

  const root = makeNode({
    id: "org_root",
    kind: "organization",
    name: "CoreMonit Organization",
    label: "Organization",
    path: "/",
    children: clientNodes,
    tags: ["root"],
    meta: { preset, serviceCount: services.length },
  }, rng);

  return { preset, root, updatedAt: new Date().toISOString() };
}
